import { Formik, Form } from 'formik';
import FormInput from 'components/FormInput';
import FormSelect from 'components/FormSelect';
import StyledButton from 'components/StyledButton';
import { WarningTriangleIcon } from '@iconicicons/react';
import * as Yup from 'yup';
import Spacer from 'components/Spacer';
import FormTextArea from 'components/FormTextArea';
import { applyMask } from 'components/FSPrivacy';

type RequestTypes = 'Amendment' | 'Cancellation' | 'General Query';
export interface ISupportFormDetails {
    email: string;
    name: string;
    enquiryType: RequestTypes;
    bookingId: string;
    comments: string;
}
interface ISupportForm {
    onSubmit: (_details: ISupportFormDetails, _resetForm: any) => void;
    defaultValues?: ISupportFormDetails;
    disableSubmit: boolean;
}

const BookingSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, 'Too Short')
        .max(50, 'Too Long')
        .required('Required'),
    email: Yup.string()
        .email('Invalid email')
        .required('Required'),
    bookingId: Yup.string()
        .max(25, 'Too Long')
        .when('requestType', {
            is: (requestType: string) => requestType !== 'General Query',
            then: Yup.string()
                .required('Please provide your booking Id')
        }),
    comments: Yup.string()
        .min(5, 'Too Short')
        .required('Required'),
});

const MaskedInput = applyMask(FormInput);

const SupportForm: React.FC<ISupportForm> = ({
    onSubmit,
    disableSubmit = false,
    defaultValues = {
        email: '',
        name: '',
        comments: '',
        enquiryType: 'Amendment',
        bookingId: ''
    },
}) => {
    return (
        <Formik
            enableReinitialize
            initialValues={{
                email: defaultValues.email,
                name: defaultValues.name,
                comments: defaultValues.comments,
                enquiryType: defaultValues.enquiryType,
                bookingId: defaultValues.bookingId
            }}
            validationSchema={BookingSchema}
            onSubmit={(values, { setSubmitting, validateForm, resetForm }) => {
                validateForm(values);
                onSubmit({ ...values, enquiryType: values.enquiryType ? values.enquiryType : 'Amendment' }, resetForm);
                setSubmitting(false);
            }}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting
            }) => (
                <Form onSubmit={(handleSubmit)}>
                    <FormSelect
                        label="Enquiry type"
                        name="enquiryType"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        options={['Amendment', 'Cancellation', 'General Query']}
                        value={values.enquiryType}
                        error={errors.enquiryType && touched.enquiryType ? errors.enquiryType : ''}
                        errorIcon={<WarningTriangleIcon />}
                    />
                    <FormInput
                        label="Booking ID"
                        type="text"
                        name="bookingId"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.bookingId}
                        error={errors.bookingId && touched.bookingId ? errors.bookingId : ''}
                        errorIcon={<WarningTriangleIcon />}
                    />
                    <Spacer vertical={16} />
                    <MaskedInput
                        label="Name"
                        type="text"
                        name="name"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.name}
                        error={
                            errors.name && touched.name ? errors.name : ''
                        }
                        errorIcon={<WarningTriangleIcon />}
                    />
                    <Spacer vertical={16} />
                    <MaskedInput
                        label="Email"
                        type="email"
                        name="email"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.email}
                        error={errors.email && touched.email ? errors.email : ''}
                        errorIcon={<WarningTriangleIcon />}
                    />
                    <Spacer vertical={16} />
                    <FormTextArea
                        label="Comment"
                        name="comments"
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.comments}
                        description=""
                        error={errors.comments && touched.comments ? errors.comments : ''}
                        errorIcon={<WarningTriangleIcon />}
                        minHeight={200}
                    />
                    <Spacer vertical={16} />
                    <StyledButton
                        title="Send"
                        type="submit"
                        disabled={isSubmitting || disableSubmit}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default SupportForm;
